import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ImageWindowProps {
  src: string;
  alt: string;
  title: string;
  onClose: () => void;
}

/** An example image shown in a browser-style window above the page. */
export function ImageWindow({
  src,
  alt,
  title,
  onClose
}: ImageWindowProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return createPortal(<motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" data-id="element-253" data-mp="1ockk8bbprnyp">
      <button type="button" aria-label="Close window" onClick={onClose} className="absolute inset-0 cursor-default bg-[var(--fg)]/60 focus:outline-none" data-id="element-254" data-mp="3505v7kct34p5" />
      <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
      opacity: 0,
      scale: 0.95,
      y: 16
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 10
    }} transition={{
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative flex max-h-[90vh] w-[min(1240px,100%)] flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_60px_120px_-40px_rgba(0,0,0,0.7)]" data-id="element-255" data-mp="2vyof41zef30g">
        <div className="flex shrink-0 items-center gap-3 border-b border-[var(--fg)]/[0.08] px-4 py-2.5" data-id="element-256" data-mp="28b8206kpfeu3">
          <div className="flex gap-1.5" aria-hidden="true" data-id="element-257" data-mp="23aaj1rrvegrj">
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-258" data-mp="1yc933fpqqxka" />
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-259" data-mp="1yebykf9nxiwe" />
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-260" data-mp="1on21m97yxf74" />
          </div>
          <span className="truncate rounded-full bg-[var(--surface)] px-3 py-1 text-[10.5px] font-medium text-[var(--muted)]" data-id="element-261" data-mp="1tohnu4lvtrr3">
            {title}
          </span>
          <button type="button" onClick={onClose} className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-full bg-[var(--surface)] px-3 text-[11px] font-semibold text-[var(--fg)] transition-colors hover:bg-[#EBE6DB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-id="element-262" data-mp="1jpa8jbvl7n1c">
            Close
            <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-263" data-mp="1xn8oumtgxtjh" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-white p-4" data-id="element-264" data-mp="22mlbb6gfgylj">
          <img src={src} alt={alt} className="block max-h-[78vh] w-auto max-w-full object-contain" data-id="element-265" data-mp="2qcnvl0e3p09l" />
        </div>
      </motion.div>
    </motion.div>, document.body);
}